import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  FaBox, FaFlask, FaXRay, FaCalendarAlt, FaClock, FaHome,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaTrash
} from "react-icons/fa";

const StaffTestPackage = () => {
  const { staffId } = useParams();
  const [packages, setPackages] = useState([]);
  const [tests, setTests] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("packages");
  const [deleting, setDeleting] = useState(null); // Track which item is being deleted

  useEffect(() => {
    if (!staffId) return;
    fetchData();
  }, [staffId]);

  const fetchData = () => {
    setLoading(true);

    const token = localStorage.getItem("authToken");

    axios
      .get(
        "https://api.elthiumhealth.com/api/staff/stafftestpackages",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        if (res.data) {
          setPackages(res.data.myPackages || []);
          setTests(res.data.myTests || []);
          setScans(res.data.myScans || []);
        }
      })
      .catch((err) => {
        console.error(
          "❌ Error fetching staff data:",
          err.response?.data || err.message
        );
        alert("Failed to load data.");
      })
      .finally(() => setLoading(false));
  };

  // Delete package handler
  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) {
      return;
    }

    setDeleting({ type: "package", id: packageId });

    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.delete(
        "https://api.elthiumhealth.com/api/admin/removepackages",
        {
          data: {
            staffId,
            packageId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setPackages((prevPackages) =>
          prevPackages.filter((pkg) => pkg._id !== packageId)
        );

        alert("Package deleted successfully!");
      } else {
        alert(`Failed to delete package: ${response.data.message}`);
      }
    } catch (error) {
      console.error(
        "Error deleting package:",
        error.response?.data || error.message
      );

      if (error.response) {
        alert(
          `Failed to delete package: ${error.response.data.message || error.response.statusText
          }`
        );
      } else if (error.request) {
        alert("Network error. Please check your connection and try again.");
      } else {
        alert("Failed to delete package. Please try again.");
      }
    } finally {
      setDeleting(null);
    }
  };

  // Delete test handler
  const handleDeleteTest = async (testId) => {
    if (!window.confirm("Are you sure you want to delete this test?")) {
      return;
    }

    setDeleting({ type: "test", id: testId });

    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.delete(
        "https://api.elthiumhealth.com/api/admin/removetests",
        {
          data: {
            staffId,
            testId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setTests((prevTests) =>
          prevTests.filter((test) => test._id !== testId)
        );

        alert("Test deleted successfully!");
      } else {
        alert(`Failed to delete test: ${response.data.message}`);
      }
    } catch (error) {
      console.error(
        "Error deleting test:",
        error.response?.data || error.message
      );

      if (error.response) {
        alert(
          `Failed to delete test: ${error.response.data.message || error.response.statusText
          }`
        );
      } else if (error.request) {
        alert("Network error. Please check your connection and try again.");
      } else {
        alert("Failed to delete test. Please try again.");
      }
    } finally {
      setDeleting(null);
    }
  };

  // Delete scan handler
  const handleDeleteScan = async (scanId) => {
    if (!window.confirm("Are you sure you want to delete this scan?")) {
      return;
    }

    setDeleting({ type: "scan", id: scanId });

    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.delete(
        "https://api.elthiumhealth.com/api/admin/removescans",
        {
          data: {
            staffId,
            scanId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setScans((prevScans) =>
          prevScans.filter((scan) => scan._id !== scanId)
        );

        alert("Scan deleted successfully!");
      } else {
        alert(`Failed to delete scan: ${response.data.message}`);
      }
    } catch (error) {
      console.error(
        "Error deleting scan:",
        error.response?.data || error.message
      );

      if (error.response) {
        alert(
          `Failed to delete scan: ${error.response.data.message || error.response.statusText
          }`
        );
      } else if (error.request) {
        alert("Network error. Please check your connection and try again.");
      } else {
        alert("Failed to delete scan. Please try again.");
      }
    } finally {
      setDeleting(null);
    }
  };

  // Transform tests data
  const transformedTests = tests.map(item => ({
    _id: item._id,
    name: item.testName || (item.testId?.name || "N/A"),
    price: item.price || (item.testId?.price || 0),
    fastingRequired: item.fastingRequired || (item.testId?.fastingRequired || false),
    homeCollectionAvailable: item.homeCollectionAvailable || (item.testId?.homeCollectionAvailable || false),
    reportIn24Hrs: item.reportIn24Hrs || (item.testId?.reportIn24Hrs || false),
    description: item.description || (item.testId?.description || ""),
    instruction: item.instruction || (item.testId?.instruction || ""),
    precaution: item.precaution || (item.testId?.precaution || ""),
    diagnosticCenter: item.diagnosticId?.name || "Not specified"
  }));

  // Transform scans data
  const transformedScans = scans.map(item => ({
    _id: item._id,
    title: item.title || (item.scanId?.title || "N/A"),
    price: item.price || (item.scanId?.price || 0),
    preparation: item.preparation || (item.scanId?.preparation || ""),
    reportTime: item.reportTime || (item.scanId?.reportTime || "24"),
    diagnosticCenter: item.diagnosticId?.name || "Not specified"
  }));

  const renderDiagnosticCenter = (center) => (
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
      <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
        <FaHome /> Diagnostic Center
      </h4>
      <div className="space-y-2">
        <p className="text-sm text-gray-700 flex items-center gap-2">
          <span className="font-medium">Name:</span> {center.name || "Not specified"}
        </p>
        {center.address && (
          <p className="text-sm text-gray-600 flex items-start gap-2">
            <FaMapMarkerAlt className="mt-1 flex-shrink-0" />
            <span>{center.address}</span>
          </p>
        )}
        {center.phone && (
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <FaPhone /> {center.phone}
          </p>
        )}
        {center.email && (
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <FaEnvelope /> {center.email}
          </p>
        )}
        {center.centerType && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Type:</span> {center.centerType}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">
            Your Diagnostic Services
          </h1>
          <p className="text-center text-gray-600 mb-8">
            View all your available packages, tests, and scans in one place
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setActiveSection("packages")}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeSection === "packages"
                ? "bg-purple-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
          >
            <FaBox />
            Packages ({packages.length})
          </button>
          <button
            onClick={() => setActiveSection("tests")}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeSection === "tests"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
          >
            <FaFlask />
            Tests ({transformedTests.length})
          </button>
          <button
            onClick={() => setActiveSection("scans")}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeSection === "scans"
                ? "bg-green-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
          >
            <FaXRay />
            Scans ({transformedScans.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-lg text-gray-600">Loading your data...</p>
          </div>
        ) : (
          <>
            {/* Packages Section */}
            {activeSection === "packages" && (
              <div>
                <h2 className="text-2xl font-bold text-purple-800 mb-6 flex items-center gap-3">
                  <FaBox className="text-3xl" />
                  Health Packages
                </h2>

                {packages.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <FaBox className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-xl text-gray-500">No packages found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {packages.map((pkg) => (
                      <div
                        key={pkg._id}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 relative"
                      >
                        {/* Package Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-6 text-white relative">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-xl font-bold">{pkg.packageName}</h3>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                                  {pkg.totalTestsIncluded} Tests Included
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              {/* Delete Button - Price ke left side */}
                              <button
                                onClick={() => handleDeletePackage(pkg._id)}
                                disabled={deleting?.type === "package" && deleting?.id === pkg._id}
                                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete Package"
                              >
                                {deleting?.type === "package" && deleting?.id === pkg._id ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <FaTrash />
                                )}
                              </button>

                              {/* Price Section */}
                              <div>
                                {pkg.offerPrice > 0 ? (
                                  <>
                                    <span className="text-2xl font-bold">₹{pkg.offerPrice}</span>
                                    <span className="block text-sm line-through opacity-80">₹{pkg.price}</span>
                                  </>
                                ) : (
                                  <span className="text-2xl font-bold">₹{pkg.price}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-6 space-y-6">
                          {/* Diagnostic Center */}
                          {pkg.diagnosticCenter && renderDiagnosticCenter(pkg.diagnosticCenter)}

                          {/* Description */}
                          {pkg.description && (
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <FaCalendarAlt /> Description
                              </h4>
                              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">
                                {pkg.description}
                              </p>
                            </div>
                          )}

                          {/* Precautions */}
                          {pkg.precautions && (
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-2">Precautions</h4>
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <pre className="text-sm text-yellow-800 whitespace-pre-wrap font-sans">
                                  {pkg.precautions}
                                </pre>
                              </div>
                            </div>
                          )}

                          {/* Included Tests */}
                          {pkg.includedTests?.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-4">Included Tests</h4>
                              <div className="grid grid-cols-1 gap-3">
                                {pkg.includedTests.map((test, idx) => (
                                  <div
                                    key={test._id || idx}
                                    className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg hover:bg-blue-100 transition-colors"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="font-semibold text-gray-800">{test.name}</p>
                                        {test.subTests.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-sm text-gray-600 mb-1">
                                              Sub-tests ({test.subTests.length}):
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                              {test.subTests.map((subTest, subIdx) => (
                                                <span
                                                  key={subIdx}
                                                  className="bg-white text-gray-700 text-xs px-3 py-1 rounded-full border"
                                                >
                                                  {subTest}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tests Section */}
            {activeSection === "tests" && (
              <div>
                <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center gap-3">
                  <FaFlask className="text-3xl" />
                  Individual Tests
                </h2>

                {transformedTests.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <FaFlask className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-xl text-gray-500">No tests found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {transformedTests.map((test) => (
                      <div
                        key={test._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300 relative"
                      >
                        <div className="p-6">
                          {/* Test Header with Delete Button */}
                          <div className="flex justify-between items-start mb-4 relative">
                            <h3 className="text-lg font-bold text-gray-800">{test.name}</h3>
                            <div className="flex items-center gap-2">
                              {/* Delete Button - Price ke left side */}
                              <button
                                onClick={() => handleDeleteTest(test._id)}
                                disabled={deleting?.type === "test" && deleting?.id === test._id}
                                className="bg-red-100 hover:bg-red-200 text-red-600 p-2 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete Test"
                              >
                                {deleting?.type === "test" && deleting?.id === test._id ? (
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <FaTrash />
                                )}
                              </button>

                              {/* Price */}
                              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                                ₹{test.price}
                              </span>
                            </div>
                          </div>

                          {/* Badges */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {test.fastingRequired && (
                              <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">
                                Fasting Required
                              </span>
                            )}
                            {test.homeCollectionAvailable && (
                              <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full">
                                Home Collection
                              </span>
                            )}
                            {test.reportIn24Hrs && (
                              <span className="bg-purple-100 text-purple-700 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                <FaClock /> 24H Report
                              </span>
                            )}
                          </div>

                          {/* Diagnostic Center */}
                          <div className="mb-4">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Center:</span> {test.diagnosticCenter}
                            </p>
                          </div>

                          {/* Description */}
                          {test.description && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-gray-700 text-sm mb-1">Description</h4>
                              <p className="text-sm text-gray-600 line-clamp-2">{test.description}</p>
                            </div>
                          )}

                          {/* Instructions */}
                          {test.instruction && (
                            <div>
                              <h4 className="font-semibold text-gray-700 text-sm mb-1">Instructions</h4>
                              <p className="text-sm text-gray-600 line-clamp-2">{test.instruction}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Scans Section */}
            {activeSection === "scans" && (
              <div>
                <h2 className="text-2xl font-bold text-green-800 mb-6 flex items-center gap-3">
                  <FaXRay className="text-3xl" />
                  Scans & X-Rays
                </h2>

                {transformedScans.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <FaXRay className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-xl text-gray-500">No scans found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {transformedScans.map((scan) => (
                      <div
                        key={scan._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300 relative"
                      >
                        <div className="p-6">
                          {/* Scan Header with Delete Button */}
                          <div className="flex justify-between items-start mb-4 relative">
                            <h3 className="text-lg font-bold text-gray-800">{scan.title}</h3>
                            <div className="flex items-center gap-2">
                              {/* Delete Button - Price ke left side */}
                              <button
                                onClick={() => handleDeleteScan(scan._id)}
                                disabled={deleting?.type === "scan" && deleting?.id === scan._id}
                                className="bg-red-100 hover:bg-red-200 text-red-600 p-2 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete Scan"
                              >
                                {deleting?.type === "scan" && deleting?.id === scan._id ? (
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <FaTrash />
                                )}
                              </button>

                              {/* Price */}
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                                ₹{scan.price}
                              </span>
                            </div>
                          </div>

                          {/* Scan Details */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FaClock />
                              <span>Report in: {scan.reportTime || "24"} hours</span>
                            </div>

                            <div className="mb-4">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Center:</span> {scan.diagnosticCenter}
                              </p>
                            </div>

                            {/* Preparation */}
                            {scan.preparation && (
                              <div>
                                <h4 className="font-semibold text-gray-700 text-sm mb-2">Preparation Required</h4>
                                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                                  <p className="text-sm text-gray-700">{scan.preparation}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffTestPackage;